import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { type DropEvent, type FileRejection, useDropzone } from 'react-dropzone';

import { ImageSearchIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import { SearchCommandGroup } from '~/components/SearchCommand/SearchCommandGroup';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { ACCEPTED_MIME_TYPES, resizeImageToBase64 } from '~/utils/imageSearch';

export type SearchCommandSimilarImageSearchProps = {
  onSearch: (base64Image: string) => void;
};

export const SearchCommandSimilarImageSearch = ({ onSearch }: SearchCommandSimilarImageSearchProps) => {
  const { showToast } = useToasts();
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(false);

  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const processImage = useCallback(
    async (file: File) => {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        showToast('Please upload a JPEG or PNG image', { color: 'red' });
        return;
      }

      setIsProcessing(true);

      try {
        const base64Image = await resizeImageToBase64(file);
        if (!isMountedRef.current) return;
        onSearch(base64Image);
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast('Failed to process image. Please try again.', { color: 'red' });
        if (isDevOrTestStage()) {
          throw error;
        } else {
          reportErrorToBugsnag({
            error,
            context: 'SearchCommandSimilarImageSearch: Image processing failed',
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsProcessing(false);
        }
      }
    },
    [onSearch, showToast],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      // Stop the event from propagating to parent dropzones
      event.stopPropagation();
      event.preventDefault();

      if (fileRejections.length > 0) {
        showToast('Please upload a JPEG or PNG image', { color: 'red' });
        return;
      } else if (acceptedFiles.length > 0) {
        processImage(acceptedFiles[0]);
      }
    },
    [processImage, showToast],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        processImage(file);
      }
      // Reset input value so the same file can be selected again
      event.target.value = '';
    },
    [processImage],
  );

  return (
    <SearchCommandGroup heading="Search by image">
      <div
        {...getRootProps()}
        className={cn(
          'flex min-h-[184px] items-center justify-center rounded-8 border border-grey-7 border-dashed px-4 py-8 transition-colors',
          isDragActive && 'border-blue-9 border-solid bg-blue-1 outline outline-1 outline-blue-9',
          isProcessing && 'pointer-events-none opacity-50',
        )}
      >
        <input
          {...getInputProps()}
          ref={fileInputRef}
          onChange={handleFileInputChange}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          className="hidden"
        />
        <div className="flex flex-col items-center gap-2 text-center">
          {isProcessing ? (
            <Spinner className="size-6 text-blue-9" />
          ) : (
            <>
              <ImageSearchIcon className="size-6 text-grey-12" />
              <p className="font-medium text-14 text-grey-12">
                {isDragActive ? 'Drop here to search' : 'Drag and drop image here to search'}
              </p>
              <p className="text-12 text-grey-10">Upload a JPG or PNG to search for visually similar assets.</p>

              <Button
                type="button"
                onClick={handleButtonClick}
                disabled={isProcessing}
                appearance="outline"
                size="medium"
                color="blue"
              >
                {isProcessing ? 'Processing...' : 'Browse files'}
              </Button>
            </>
          )}
        </div>
      </div>
    </SearchCommandGroup>
  );
};
